/**
 * ═════════════════════════════════════════════════════════════════════════════
 * PRINT-SHEET READINESS — don't let `window.print()` snapshot a sheet whose
 * images are still in flight.
 *
 * ★ WHY THIS EXISTS (reported 2026-09-11, against the LEGACY app on staging).
 *
 * The Fan rating dialog printed a report with an EMPTY manufacturer-logo box.
 * The reporter's Network panel showed the cause outright: the logo request,
 * `/media/mfg/logo/UMP_Logo_with_Name_9uv0NUu.png`, sat `(pending)` while the
 * PDF was being produced. Three things line up:
 *
 *  1. `/media` is NOT served as files on dev/staging — nginx proxies it to
 *     gunicorn (`location /media { proxy_pass http://<env>_gunicorn; }`), so a
 *     logo costs a worker.
 *  2. That upstream is TWO sync workers (`--workers 2`).
 *  3. Opening the rating dialog fires ~100 Django calls (measured: 102
 *     requests, 340-550 ms each, over an 18 s span). The logo queues behind
 *     them.
 *
 * …and both apps then print on a BLIND fixed timer — legacy
 * `setTimeout(window.print, 200)` (fan.selector.js:8376, coil.selector.js:4187
 * /:4202), which the React ports carried over verbatim. 200 ms is plenty for
 * the compiled sheet and the curve canvas to paint, which is all legacy was
 * deferring for; it is NOT a wait on an image that has to get through a
 * two-worker queue. When the image loses that race Chrome snapshots the page
 * without it, so the box prints empty with no console error and no 404.
 *
 * It reads as intermittent because Cloudflare caches `/media` for four hours —
 * the second print in the same window usually hits cache and looks fine.
 *
 * dev3 hides it: dev3 serves `/media` from `www/media` as files, so the logo
 * never queues. The bug would have appeared on the first print after promotion.
 *
 * ★ WHAT THIS CHANGES. Nothing about the sheet, its markup or the 200 ms defer.
 * The print is simply held until the sheet's images have settled as well — the
 * defer becomes `max(200 ms, images)` rather than `200 ms` alone, so the common
 * (cached) case is unchanged at 200 ms.
 *
 * ★ THE CEILING IS LOAD-BEARING. A logo that never resolves must not cost the
 * user their report: after `ceilingMs` the print goes ahead without it, which
 * is exactly today's behaviour. This can only ever delay a print, never block
 * one.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** The 200 ms legacy defers for the compiled template + curve canvas to paint. */
export const PRINT_PAINT_DELAY_MS = 200;

/** Longest we will hold a print waiting on a stuck image. */
const DEFAULT_CEILING_MS = 3000;

const settled = (img: HTMLImageElement): Promise<void> =>
  // `complete` covers loaded, errored and empty-src alike, so a broken logo
  // resolves immediately instead of waiting out the ceiling.
  img.complete
    ? Promise.resolve()
    : new Promise((resolve) => {
        const done = (): void => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      });

/**
 * Resolve once the mounted print sheet is safe to snapshot: the paint defer has
 * elapsed AND every `<img>` inside it has settled — or `ceilingMs` has passed,
 * whichever comes first.
 *
 * Call it from the effect that fires `window.print()`, with the sheet already
 * committed to the DOM (a `useEffect` body always is).
 */
export const printSheetReady = (
  root: ParentNode = document,
  ceilingMs: number = DEFAULT_CEILING_MS
): Promise<void> => {
  const paint = new Promise<void>((resolve) => {
    window.setTimeout(resolve, PRINT_PAINT_DELAY_MS);
  });
  const images = Promise.all(
    Array.from(root.querySelectorAll<HTMLImageElement>('.print-content img')).map(settled)
  ).then(() => undefined);
  const ceiling = new Promise<void>((resolve) => {
    window.setTimeout(resolve, ceilingMs);
  });
  return Promise.race([Promise.all([paint, images]).then(() => undefined), ceiling]);
};
